import { useFindMany } from "@gadgetinc/react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/16/solid";
import { useCallback, useEffect, useState } from "react";
import { api } from "../../api";
import { formatDateTime } from "../../functions/date-utils";
import LoadingSpinner from "../LoadingSpinner";
import { Button } from "../tailwind/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../tailwind/table";
import { Text } from "../tailwind/text";

export function SalesList({ campaign, shop, user, filterDates }) {
  const NUM_ON_PAGE = 4;
  const [cursor, setCursor] = useState({ first: NUM_ON_PAGE });
  const [pageNumber, setPageNumber] = useState(1);
  const [{ data, fetching }, refresh] = useFindMany(api.shopifyOrder, {
    ...cursor,
    filter: [
      { campaignOriginId: { equals: campaign } },
      { shopId: { equals: shop?.id } },
      {
        AND: [
          { processedAt: { greaterThan: filterDates.startDate } },
          { processedAt: { lessThan: filterDates.endDate } },
        ],
      },
    ],
    select: {
      id: true,
      shopifyUpdatedAt: true,
      name: true,
      totalPrice: true,
      processedAt: true,
      customer: {
        firstName: true,
        lastName: true,
      },
    },
    sort: {
      processedAt: "Descending",
    },
  });

  // Refresh when user is charged
  useEffect(() => {
    if (user && shop) {
      refresh();
    }
  }, [user, shop]);

  const getNextPage = useCallback(() => {
    setCursor({ first: NUM_ON_PAGE, after: data.endCursor });
    setPageNumber(pageNumber + 1);
  }, [data]);

  const getPreviousPage = useCallback(() => {
    setCursor({ last: NUM_ON_PAGE, before: data.startCursor });
    setPageNumber(pageNumber - 1);
  }, [data]);

  const handleRowClick = useCallback(
    (id) => {
      if (shop) {
        window.open(
          `https://admin.shopify.com/store/${shop.myshopifyDomain.split(".myshopify.com")[0]}/orders/${id}`,
          "_blank",
        );
      }
    },
    [shop],
  );

  return (
    <>
      {fetching && (
        <div className="h-[12em]">
          <LoadingSpinner />
        </div>
      )}

      <div className="flex flex-col justify-between grow">
        {!fetching && (
          <div className=" text-xs md:text-sm">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader className="w-[30%]">Name</TableHeader>
                  <TableHeader className="w-[20%]">Order number</TableHeader>
                  <TableHeader className="text-center w-[20%]">
                    Amount
                  </TableHeader>
                  <TableHeader className="w-[30%] hidden sm:table-cell">
                    Order date
                  </TableHeader>
                </TableRow>
              </TableHead>
              <TableBody className="h-[8em]">
                {data?.length > 0 ? (
                  data.map(
                    (
                      { id, processedAt, name, totalPrice, customer },
                      index,
                    ) => (
                      <TableRow
                        key={id}
                        id={id}
                        position={index}
                        onClick={shop ? () => handleRowClick(id) : undefined}
                      >
                        <TableCell>
                          {customer?.firstName} {customer?.lastName}
                        </TableCell>
                        <TableCell>{name}</TableCell>
                        <TableCell className="text-center">
                          {totalPrice
                            ? parseFloat(totalPrice)?.toFixed(2) + "€"
                            : ""}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {formatDateTime(processedAt, "shortTimeMediumDate")}
                        </TableCell>
                      </TableRow>
                    ),
                  )
                ) : (
                  <>
                    <TableRow>
                      <TableCell colSpan={4} className="text-center">
                        No attributed sales
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="flex justify-between mt-4">
          <Button
            color="secondaryButton"
            onClick={getPreviousPage}
            disabled={!data?.hasPreviousPage}
          >
            <ArrowLeftIcon />
            Previous
          </Button>
          <Text>{pageNumber}</Text>
          <Button
            color="secondaryButton"
            onClick={getNextPage}
            disabled={!data?.hasNextPage}
          >
            Next
            <ArrowRightIcon />
          </Button>
        </div>
      </div>
    </>
  );
}
